import { omit, uniq } from 'ramda';
import { isBefore, isSameDay, isValid } from 'date-fns';
import type {
    PopDisplays_VirtualCampaignSummary,
    VirtualCampaignBuilderSummaryPayload,
} from 'c-sdk';

export type VirtualCampaignFormValues = VirtualCampaignBuilderSummaryPayload & {
    media_item_names: string[]; // from the autocomplete
    media_item_text: string; // from the autocomplete
};

/**
 * The summary request: media picked in the autocomplete plus media typed one per line (blank lines
 * and surrounding spaces dropped, duplicates removed), with the dates widened to whole days.
 */
export const buildVirtualCampaignPayload = (
    data: VirtualCampaignFormValues,
): VirtualCampaignBuilderSummaryPayload => ({
    ...omit(['media_item_names', 'media_item_text'], data),
    media_items: uniq([
        ...(data.media_item_names ?? []),
        ...(data.media_item_text
            ?.split('\n')
            ?.map(id => id.trim())
            .filter(id => id.length > 0) ?? []),
    ]),
    owner_ids: Array.isArray(data.owner_ids) ? data.owner_ids : [],
    start_date: `${data.start_date} 00:00:00`,
    end_date: `${data.end_date} 23:59:59`,
});

/** True when both dates are real and the end is before the start (the same day is fine). */
export const virtualCampaignDatesInvalid = (start: Date, end: Date): boolean => {
    if (isValid(start) && isValid(end)) return !isBefore(start, end) && !isSameDay(start, end);

    return false;
};

/**
 * The playback dialog's title: the display that was clicked (its address, else its name), else
 * the frame's first display, else nothing.
 */
export const playbackSummaryName = (
    summary: PopDisplays_VirtualCampaignSummary | null | undefined,
    displayId: number | null,
): string => {
    if (!summary) return '';
    const displays = summary.channelFrame?.displays;

    const selected =
        Array.isArray(displays) && displayId != null
            ? displays.find(d => d.id === displayId)
            : null;

    const fromDisplay =
        selected?.address ??
        selected?.name ??
        (Array.isArray(displays) && displays.length > 0
            ? displays[0].address ?? displays[0].name
            : undefined);

    return fromDisplay ?? '';
};
